import React, { useState, useEffect } from 'react';
import { getCurrentUser, logout as authLogout, setToken as storeToken } from './utils/authService';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import SessionsPage from './pages/SessionsPage';
import AnalysisPage from './pages/AnalysisPage';
import AdminDashboard, { ADMIN_EMAIL } from './pages/AdminDashboard';
import TrackViewerPage from './pages/TrackViewerPage';
import ProfilePage from './pages/ProfilePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ImportPage from './pages/ImportPage';
import LeaderboardPage from './pages/LeaderboardPage';
import BlogPage from './pages/BlogPage';
import LoadingProgress from './components/LoadingProgress';

function checkIsAdmin(email, role) {
  return email === ADMIN_EMAIL || role === 'admin';
}

// Parse deep-link params once at module load — survives React StrictMode double-mounting
// (StrictMode remounts components, but module-level code only runs once)
const _launchParams = new URLSearchParams(window.location.search);
const _launchToken = _launchParams.get('token');
const _launchPage = _launchParams.get('page');
const _launchResetEmail = _launchParams.get('resetemail');
// Clear query params immediately so they aren't visible or reused on refresh
if (_launchToken || _launchPage || _launchResetEmail) {
  window.history.replaceState({}, document.title, window.location.pathname);
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [currentPage, setCurrentPage] = useState('login');
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [returnPage, setReturnPage] = useState(null);
  const [sessionsTrackFilter, setSessionsTrackFilter] = useState(null);
  const [profileTarget, setProfileTarget] = useState(null);
  const [resetEmailHint, setResetEmailHint] = useState(null);
  const [embedded, setEmbedded] = useState(_launchPage === 'import');

  // Expose a global hook so the MAUI WebView can force-navigate to import
  // after page load (fallback if query params are lost during navigation)
  useEffect(() => {
    window.__rvNavigateImport = () => {
      if (currentPage !== 'import') {
        setEmbedded(true);
        setCurrentPage('import');
      }
    };
    return () => { delete window.__rvNavigateImport; };
  });

  // Check for existing JWT session on mount
  useEffect(() => {
    async function checkAuth() {
      // Check for #leaderboard hash for direct public access
      if (window.location.hash === '#leaderboard') {
        setCurrentPage('leaderboard');
        setAuthReady(true);
        return;
      }
      // #blog (or #blog/<slug>) is public, but still load the user if signed in
      // so the blog shows the logged-in nav (Dashboard / Back) rather than Sign In.
      const wantsBlog = window.location.hash.startsWith('#blog');
      // Use module-level params (already parsed and cleared above)
      const hint = _launchResetEmail;
      const tokenParam = _launchToken;
      const isEmbeddedImport = _launchPage === 'import';
      if (hint) setResetEmailHint(hint);
      if (tokenParam) storeToken(tokenParam);
      try {
        const u = await getCurrentUser();
        if (u) {
          setUser(u);
          setCurrentPage(u.mustChangePassword ? 'reset-password' : (wantsBlog ? 'blog' : (isEmbeddedImport ? 'import' : 'sessions')));
        } else {
          setUser(null);
          setCurrentPage(wantsBlog ? 'blog' : 'login');
        }
      } catch {
        setUser(null);
        setCurrentPage(wantsBlog ? 'blog' : 'login');
      }
      setAuthReady(true);
    }
    checkAuth();
  }, []);

  // Auto-logout after 10 minutes of inactivity
  useEffect(() => {
    if (!user) return;
    const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
    let timer = setTimeout(() => {
      authLogout();
      setUser(null);
      setCurrentPage('login');
    }, INACTIVITY_TIMEOUT);
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        authLogout();
        setUser(null);
        setCurrentPage('login');
      }, INACTIVITY_TIMEOUT);
    };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  useEffect(() => {
    if (user?.mustChangePassword && currentPage !== 'reset-password') {
      setCurrentPage('reset-password');
    }
  }, [user, currentPage]);

  const handleLogin = (loggedInUser) => {
    setUser(loggedInUser);
    setCurrentPage(loggedInUser.mustChangePassword ? 'reset-password' : (embedded ? 'import' : 'sessions'));
  };

  const handleLogout = () => {
    authLogout();
    setUser(null);
    setCurrentPage('login');
  };

  const navigateTo = (page, data) => {
    if (user?.mustChangePassword && page !== 'reset-password') {
      setCurrentPage('reset-password');
      return;
    }
    if (page === 'profile' || page === 'trackViewer' || page === 'import') {
      setReturnPage(currentPage);
    }
    setCurrentPage(page);
    if (page === 'analysis') setSelectedSession(data);
    if (page === 'trackViewer') setSelectedTrack(data);
    if (page === 'sessions') setSessionsTrackFilter(data?.trackFilter || null);
    if (page === 'profile') setProfileTarget(data || null);
  };

  // Show nothing while checking auth state
  if (!authReady) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--color-bg)',
      }}>
        <div style={{ width: '100%', maxWidth: 500 }}>
          <LoadingProgress label="Starting RaceVoice" expectedMs={2000} />
        </div>
      </div>
    );
  }

  // Not logged in — show login, signup, or public leaderboard
  if (!user) {
    if (currentPage === 'leaderboard') {
      return <LeaderboardPage onLogin={() => setCurrentPage('login')} />;
    }
    if (currentPage === 'signup') {
      return <SignUpPage onNavigateLogin={() => setCurrentPage('login')} />;
    }
    if (currentPage === 'blog') {
      return <BlogPage onLogin={() => setCurrentPage('login')} />;
    }
    return (
      <LoginPage
        onLogin={handleLogin}
        onNavigateSignUp={() => setCurrentPage('signup')}
        onLeaderboard={() => setCurrentPage('leaderboard')}
        onBlog={() => setCurrentPage('blog')}
        resetEmailHint={resetEmailHint}
      />
    );
  }

  // Common nav props for all pages
  const isAdmin = checkIsAdmin(user.email, user.role);
  const forcePasswordChange = user.mustChangePassword === true;
  const navProps = {
    user,
    onLogout: handleLogout,
    onDashboard: forcePasswordChange ? undefined : () => navigateTo('sessions'),
    onAdmin: !forcePasswordChange && isAdmin ? () => navigateTo('admin') : undefined,
    onProfile: forcePasswordChange ? undefined : () => navigateTo('profile'),
    onBlog: forcePasswordChange ? undefined : () => navigateTo('blog'),
  };

  // Logged in — route to appropriate page
  switch (currentPage) {
    case 'admin':
      if (!isAdmin) {
        setCurrentPage('sessions');
        return null;
      }
      return (
        <AdminDashboard
          {...navProps}
          onDashboard={undefined}
          onViewSessions={() => navigateTo('sessions')}
          onViewTrack={(track) => navigateTo('trackViewer', track)}
          onSelectSession={(session) => navigateTo('analysis', session)}
          onEditProfile={(targetUser) => navigateTo('profile', targetUser)}
        />
      );
    case 'trackViewer':
      return (
        <TrackViewerPage
          {...navProps}
          track={selectedTrack}
          isAdmin={isAdmin}
          onBack={() => navigateTo(returnPage || (isAdmin ? 'admin' : 'sessions'))}
        />
      );
    case 'analysis':
      return (
        <AnalysisPage
          {...navProps}
          session={selectedSession}
          onBack={() => navigateTo('sessions', { trackFilter: selectedSession?.track })}
          onDashboard={() => navigateTo('sessions', { trackFilter: selectedSession?.track })}
        />
      );
    case 'profile':
      return (
        <ProfilePage
          {...navProps}
          targetUser={profileTarget}
          onBack={() => navigateTo(returnPage || (isAdmin ? 'admin' : 'sessions'))}
          onUserUpdate={(updates) => setUser((prev) => ({ ...prev, ...updates }))}
        />
      );
    case 'reset-password':
      return (
        <ResetPasswordPage
          user={user}
          onLogout={handleLogout}
          onPasswordChanged={() => {
            setUser((prev) => ({ ...prev, mustChangePassword: false }));
            setCurrentPage('sessions');
          }}
        />
      );
    case 'import':
      return (
        <ImportPage
          {...navProps}
          embedded={embedded}
          onBack={() => navigateTo(returnPage || (isAdmin ? 'admin' : 'sessions'))}
        />
      );
    case 'blog':
      return (
        <BlogPage
          {...navProps}
          onBlog={undefined}
          onBack={() => navigateTo('sessions')}
        />
      );
    case 'sessions':
    default:
      return (
        <SessionsPage
          {...navProps}
          onDashboard={undefined}
          initialTrackFilter={sessionsTrackFilter}
          onSelectSession={(session) => navigateTo('analysis', session)}
          onViewTrack={(track) => navigateTo('trackViewer', track)}
          onImport={() => navigateTo('import')}
        />
      );
  }
}
